/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import {
  DefaultButton,
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  PrimaryButton,
  Separator,
  Text,
} from "@fluentui/react";

import {
  buttonStyles,
  messageBarStyles,
  panelContentStyles,
  panelHeaderStyles,
} from "./SidePanel.styles";
import { ISidePanelProps } from "./SidePanelProps";

export const SidePanel = (props: ISidePanelProps): React.ReactElement => {
  const {
    isOpen,
    title,
    saveButtonLabel,
    cancelButtonLabel,
    error,
    disableActions,
    hideFooter,
    onDismiss,
    onSave,
    onCancel,
    additionalFooterContent,
  } = props;

  const onRenderHeaderContent = (): JSX.Element => <Text styles={panelHeaderStyles}>{title}</Text>;

  const onRenderFooterContent = (): JSX.Element => (
    <>
      {!!error ? (
        <MessageBar messageBarType={MessageBarType.warning} styles={messageBarStyles}>
          {error}
        </MessageBar>
      ) : null}
      <div>
        <Separator />
        {!!onSave && (
          <PrimaryButton
            onClick={onSave}
            styles={buttonStyles}
            disabled={disableActions}
            type="submit"
          >
            {!!saveButtonLabel ? saveButtonLabel : "Save"}
          </PrimaryButton>
        )}
        <DefaultButton
          onClick={onCancel ?? onDismiss}
          styles={buttonStyles}
          disabled={disableActions}
        >
          {!!cancelButtonLabel ? cancelButtonLabel : "Cancel"}
        </DefaultButton>
        {additionalFooterContent && <div>{additionalFooterContent}</div>}
      </div>
    </>
  );

  return (
    <>
      <Panel
        isOpen={isOpen}
        onDismiss={onDismiss}
        type={PanelType.medium}
        closeButtonAriaLabel="Close"
        onRenderHeader={onRenderHeaderContent}
        onRenderFooterContent={!hideFooter ? onRenderFooterContent : undefined}
        isFooterAtBottom={true}
        styles={panelContentStyles}
      >
        {props.children}
      </Panel>
    </>
  );
};
